import { Fragment } from 'react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { Avatar, DateWithTooltip, RouterLink, Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import {
  type PullRequestEventData,
  type TimelineEventItem,
  getPullRequestEventVisual,
} from '../util';
import LabelBox from './LabelBox';
import PullRequestStateIcon from './PullRequestStateIcon';

type Props = Readonly<{
  events: ReadonlyArray<TimelineEventItem>;
  companySlug: string;
  allUsers: ReadonlyArray<Api.User.BaseView>;
}>;

function joinNodes(nodes: React.ReactNode[]): React.ReactNode {
  return nodes.map((node, i) => (
    <Fragment key={i}>
      {i > 0 ? (i === nodes.length - 1 ? ' and ' : ', ') : null}
      {node}
    </Fragment>
  ));
}

const plural = (count: number, word: string) => (count > 1 ? `${word}s` : word);

function skillNodes(
  events: ReadonlyArray<TimelineEventItem>
): React.ReactNode[] {
  return events.map((e) => {
    const data = e.data as { skillType?: string } | null;
    return (
      <Text key={e.id} typeface="body.small.regular" bold color="neutral100">
        {data?.skillType}
      </Text>
    );
  });
}

function userName(
  users: ReadonlyArray<Api.User.BaseView>,
  userId: string | null | undefined
): string {
  const user = users.find((u) => u.id === userId);
  return user ? `${user.firstName} ${user.lastName}` : 'a user';
}

export default function TimelineEvent({
  events,
  companySlug,
  allUsers,
}: Props) {
  const event = events[0];
  const count = events.length;
  const user = event.user;
  const userPart = user ? (
    <Text typeface="body.small.regular" bold color="neutral100">
      {user.firstName} {user.lastName}&nbsp;
    </Text>
  ) : (
    'Unknown'
  );
  const datePart = <DateWithTooltip timestamp={event.createdAt} />;
  const pullRequestVisual = getPullRequestEventVisual(event);

  let inner = null;

  switch (event.type) {
    case 'close':
      inner = (
        <>
          {userPart} closed this ticket {datePart}
        </>
      );
      break;
    case 'open':
      inner = (
        <>
          {userPart} opened this ticket {datePart}
        </>
      );
      break;
    case 'label_add':
    case 'label_remove': {
      const labelNodes = events
        .filter((e) => e.label)
        .map((e) => (
          <html.span key={e.id} style={styles.labelInline}>
            <LabelBox label={e.label!} />
          </html.span>
        ));
      const verb = event.type === 'label_add' ? 'added' : 'removed';
      inner = (
        <>
          {userPart} {verb} the {joinNodes(labelNodes)}{' '}
          {plural(labelNodes.length, 'label')} {datePart}
        </>
      );
      break;
    }
    case 'mention':
      if (!event.mentionedByTicketNumber) {
        throw new Error(`${event['@id']} has no mentionedByTicketNumber`);
      }
      inner = (
        <>
          {userPart} mentioned this ticket in{' '}
          <RouterLink
            to={`/company/${companySlug}/tickets/${event.mentionedByTicketNumber}`}
          >
            #{event.mentionedByTicketNumber}
          </RouterLink>
        </>
      );
      break;
    case 'assignee_change': {
      const data = event.data as {
        oldAssigneeId: string | null;
        newAssigneeId: string | null;
      } | null;
      inner = data?.newAssigneeId ? (
        <>
          {userPart} assigned this ticket {datePart}
        </>
      ) : (
        <>
          {userPart} unassigned this ticket {datePart}
        </>
      );
      break;
    }
    case 'rename':
      inner = (
        <>
          {userPart} renamed this ticket from{' '}
          <Text
            typeface="body.small.regular"
            bold
            lineThrough
            color="neutral100"
          >
            {event.oldTitle}
          </Text>{' '}
          to{' '}
          <Text typeface="body.small.regular" bold color="neutral100">
            {event.newTitle}
          </Text>{' '}
          {datePart}
        </>
      );
      break;
    case 'skill_add':
    case 'skill_remove': {
      const verb = event.type === 'skill_add' ? 'added' : 'removed';
      inner = (
        <>
          {userPart} {verb} the {joinNodes(skillNodes(events))}{' '}
          {plural(count, 'skill')} {datePart}
        </>
      );
      break;
    }
    case 'skill_approval_change': {
      const data = event.data as { newApproved?: boolean | null } | null;
      const newApproved = data?.newApproved ?? null;
      inner =
        newApproved === null ? (
          <>
            {userPart} cleared the {joinNodes(skillNodes(events))}{' '}
            {plural(count, 'skill')} approval {datePart}
          </>
        ) : (
          <>
            {userPart} {newApproved ? 'approved' : 'rejected'} the{' '}
            {joinNodes(skillNodes(events))} {plural(count, 'skill')} {datePart}
          </>
        );
      break;
    }
    case 'skill_assignee_change': {
      const data = event.data as { newAssigneeId?: string | null } | null;
      inner = data?.newAssigneeId ? (
        <>
          {userPart} assigned{' '}
          <Text typeface="body.small.regular" bold color="neutral100">
            {userName(allUsers, data.newAssigneeId)}
          </Text>{' '}
          to the {joinNodes(skillNodes(events))} {plural(count, 'skill')}{' '}
          {datePart}
        </>
      ) : (
        <>
          {userPart} unassigned the {joinNodes(skillNodes(events))}{' '}
          {plural(count, 'skill')} {datePart}
        </>
      );
      break;
    }
    case 'skill_contributor_add':
    case 'skill_contributor_remove': {
      const data = event.data as { skillType: string } | null;
      const nameNodes = events.map((e) => {
        const d = e.data as { userId?: string } | null;
        return (
          <Text
            key={e.id}
            typeface="body.small.regular"
            bold
            color="neutral100"
          >
            {userName(allUsers, d?.userId)}
          </Text>
        );
      });
      const isAdd = event.type === 'skill_contributor_add';
      inner = (
        <>
          {userPart} {isAdd ? 'added' : 'removed'} {joinNodes(nameNodes)}{' '}
          {isAdd ? 'to' : 'from'} the{' '}
          <Text typeface="body.small.regular" bold color="neutral100">
            {data?.skillType}
          </Text>{' '}
          skill {datePart}
        </>
      );
      break;
    }
    case 'skill_status_change': {
      const data = event.data as { newStatus: string | null } | null;
      inner = data?.newStatus ? (
        <>
          {userPart} changed the {joinNodes(skillNodes(events))} skill status to{' '}
          <Text typeface="body.small.regular" bold color="neutral100">
            {data.newStatus}
          </Text>{' '}
          {datePart}
        </>
      ) : (
        <>
          {userPart} cleared the {joinNodes(skillNodes(events))} skill status{' '}
          {datePart}
        </>
      );
      break;
    }
    default:
      if (pullRequestVisual) {
        const data = event.data as PullRequestEventData | null;
        inner = (
          <>
            <html.a
              href={data?.url}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.pullRequestLink}
            >
              <Text typeface="body.small.medium" color="neutral100" underline>
                #{data?.number} {data?.title}
              </Text>
            </html.a>{' '}
            {pullRequestVisual.label} {datePart}
          </>
        );
      } else {
        inner = `Unknown event (${event.type})`;
      }
  }

  return (
    <html.div
      style={[
        Style.row,
        Style.alignCenter,
        styles.container,
        event.isInternal && styles.internal,
      ]}
    >
      <html.div style={styles.avatarWrap}>
        <Avatar user={user} size={32} />
      </html.div>
      <html.div style={[!pullRequestVisual && Style.fill]}>
        <Text typeface="body.small.regular" color="neutral60">
          {inner}
        </Text>
      </html.div>
      {pullRequestVisual && (
        <html.div style={styles.stateIcon}>
          <PullRequestStateIcon visual={pullRequestVisual} />
        </html.div>
      )}
    </html.div>
  );
}

const styles = css.create({
  container: {
    position: 'relative',
    gap: 8,
    padding: 8,
  },
  internal: {
    backgroundColor: color.neutral10,
    borderRadius: 24,
  },
  avatarWrap: {
    flexShrink: 0,
  },
  labelInline: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginInline: 5,
  },
  pullRequestLink: {
    color: color.neutral100,
    textDecorationLine: 'underline',
  },
  stateIcon: {
    display: 'flex',
    flexShrink: 0,
  },
});
